import type {
  Route,
  SidebarOptionName,
  Tags,
  TagName,
} from "@/types/UpNavRoutesType";

// Route Paths
const upNavRoutePaths = {
  // Root

  root: { name: "root", path: "/" },

  // Monitor
  monitorUpsell: { name: "monitorUpsell", path: "/upsell/monitor" },
  monitorSearch: { name: "monitorSearch", path: "/search" },
  monitorMentions: { name: "monitorMentions", path: "/mentions" },
  monitorTopics: { name: "monitorTopics", path: "/topics" },
  monitorSocial: { name: "monitorSocial", path: "/social" },
  monitorAlertsRealTime: {
    name: "monitorAlertsRealTime",
    path: "/alerts/real-time",
  },
  monitorAlertsDigest: { name: "monitorAlertsDigest", path: "/alerts/digest" },
  monitorManage: { name: "monitorManage", path: "/manage" },
  monitorManageTargetMediaLists: {
    name: "monitorManageTargetMediaLists",
    path: "/target-media-lists",
  },
  monitorManageAddedMentions: {
    name: "monitorManageAddedMentions",
    path: "/manage/added-mentions",
  },
  monitorManageExcludedMentions: {
    name: "monitorManageExcludedMentions",
    path: "/manage/excluded-mentions",
  },
  monitorManageRequestedPublications: {
    name: "monitorManageRequestedPublications",
    path: "/manage/requested-publications",
  },
  monitorManageExcludedPublications: {
    name: "monitorManageExcludedPublications",
    path: "/manage/excluded-publications",
  },
  monitorReports: { name: "monitorReports", path: "/reports" },
  monitorReportsList: { name: "monitorReportsList", path: "/reports#/list" },
  monitorReportsTemplates: {
    name: "monitorReportsTemplates",
    path: "/reports#/templates",
  },
  monitorReportsTemplatesNew: {
    name: "monitorReportsTemplatesNew",
    path: "/reports#/templates/new",
  },
  monitorReportsEdit: {
    name: "monitorReportsEdit",
    path: "/reports#/:id/edit",
  },
  monitorReportsTemplatesEdit: {
    name: "monitorReportsTemplatesEdit",
    path: "/reports#/templates/:id/edit",
  },

  chartAnalytics: { name: "chartAnalytics", path: "/charts" },

  chartAnalyticsReports: {
    name: "chartAnalyticsReports",
    path: "/pro/#/monitor/reports",
  },

  // Analytics
  analytics: { name: "analytics", path: "/analytics" },

  // Contact
  contactUpsell: { name: "contactUpsell", path: "/upsell/contact" },

  // Settings
  settings: { name: "settings", path: "/settings" },
};

// Tags
const tags: Record<TagName, Tags> = {
  new: {
    name: "new",
    translationKey: "navbar.tags.new",
  },
};

// child routes that do not appear in the navbar or tabs
const otherOptions = {
  reports: [
    {
      type: "otherOption",
      path: upNavRoutePaths.monitorReportsEdit.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorReportsEdit.name,
    },
  ],
  reportsTemplates: [
    {
      type: "otherOption",
      path: upNavRoutePaths.monitorReportsTemplatesNew.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorReportsTemplatesNew.name,
    },
    {
      type: "otherOption",
      path: upNavRoutePaths.monitorReportsTemplatesEdit.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorReportsTemplatesEdit.name,
    },
  ],
  chartAnalytics: [
    {
      type: "otherOption",
      path: upNavRoutePaths.chartAnalyticsReports.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.chartAnalyticsReports.name,
    },
  ],
};

const tabOptions = {
  alerts: [
    {
      type: "tabOption",
      translationKey: "navbar.alerts.tabs.realTime",
      path: upNavRoutePaths.monitorAlertsRealTime.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorAlertsRealTime.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.alerts.tabs.digest",
      path: upNavRoutePaths.monitorAlertsDigest.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorAlertsDigest.name,
    },
  ],
  mentions: [
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.excludedMentions",
      path: upNavRoutePaths.monitorManageExcludedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageExcludedMentions.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.requestedPublications",
      path: upNavRoutePaths.monitorManageRequestedPublications.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageRequestedPublications.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.tabs.excludedPublications",
      path: upNavRoutePaths.monitorManageExcludedPublications.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageExcludedPublications.name,
    },
    {
      type: "tabOption",
      translationKey: "navbar.manage.mentionsAndPublications",
      path: upNavRoutePaths.monitorManageAddedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageAddedMentions.name,
    },
  ],
};

const dropDownOptions = {
  manage: [
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.targetMediaLists",
      path: upNavRoutePaths.monitorManageTargetMediaLists.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageTargetMediaLists.name,
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.alerts",
      path: upNavRoutePaths.monitorAlertsRealTime.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorAlertsRealTime.name,
      childRoutes: [...tabOptions.alerts],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.manageSocialAccount",
      path: upNavRoutePaths.monitorSocial.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorSocial.name,
      tags: [tags.new],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.mentionsAndPublications",
      path: upNavRoutePaths.monitorManageAddedMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManageAddedMentions.name,
      childRoutes: [...tabOptions.mentions],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "navbar.manage.manageSearch",
      path: upNavRoutePaths.monitorManage.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManage.name,
    },
  ],
  reports: [
    {
      type: "navbarDropdownOption",
      translationKey: "common.reports",
      path: upNavRoutePaths.monitorReportsList.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.monitorReportsList.name,
      childRoutes: [...otherOptions.reports],
    },
    {
      type: "navbarDropdownOption",
      translationKey: "common.templates",
      path: upNavRoutePaths.monitorReportsTemplates.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.monitorReportsTemplates.name,
      childRoutes: [...otherOptions.reportsTemplates],
    },
  ],
};

const navbarOptions = {
  monitor: [
    {
      type: "navbarOption",
      translationKey: "navbar.monitor.search",
      path: upNavRoutePaths.monitorSearch.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorSearch.name,
    },
    {
      type: "navbarOption",
      translationKey: "common.mentions",
      path: upNavRoutePaths.monitorMentions.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorMentions.name,
    },
    {
      type: "navbarOption",
      translationKey: "common.topics",
      path: upNavRoutePaths.monitorTopics.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorTopics.name,
    },
    {
      type: "navbarOption",
      translationKey: "common.manage",
      path: upNavRoutePaths.monitorManage.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.monitorManage.name,
      childRoutes: [...dropDownOptions.manage],
    },
    {
      type: "navbarOption",
      translationKey: "common.reports",
      path: upNavRoutePaths.monitorReports.path,
      isExternalRoute: true,
      dataTarget: upNavRoutePaths.monitorReports.name,
      childRoutes: [...dropDownOptions.reports],
    },
    {
      type: "navbarOption",
      translationKey: "navbar.monitor.chartAnalytics",
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.chartAnalytics.name,
      path: upNavRoutePaths.chartAnalytics.path,
      childRoutes: [...otherOptions.chartAnalytics],
    },
  ],
  settings: [
    {
      type: "navbarOption",
      translationKey: "navbar.monitor.settings",
      path: upNavRoutePaths.settings.path,
      isExternalRoute: false,
      dataTarget: upNavRoutePaths.settings.name,
    },
  ],
};

const sidebarOptions: Partial<Record<SidebarOptionName, Route>> = {
  Monitor: {
    name: "Monitor",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.monitorSearch.name,
    translationKey: "common.monitor",
    icon: "material-symbols:browse-activity-outline",
    isPrefix: true,
    path: upNavRoutePaths.monitorSearch.path,
    upsellPath: upNavRoutePaths.monitorUpsell.path,
    childRoutes: [...navbarOptions.monitor] as Route[],
  },
  Review: {
    name: "Review",
    type: "sidebarOption",
    dataTarget: "Review",
    translationKey: "common.review",
    icon: "material-symbols:newsmode-outline",
    isPrefix: true,
  },
  Contact: {
    name: "Contact",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.contactUpsell.name,
    translationKey: "common.contact",
    icon: "material-symbols:contact-page-outline",
    isPrefix: true,
    upsellPath: upNavRoutePaths.contactUpsell.path,
  },
  Analytics: {
    name: "Analytics",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.analytics.name,
    translationKey: "common.analytics",
    icon: "material-symbols:area-chart-outline",
    isPrefix: true,
    path: upNavRoutePaths.analytics.path,
  },
  Settings: {
    name: "Settings",
    type: "sidebarOption",
    dataTarget: upNavRoutePaths.settings.name,
    translationKey: "sidebar.settings",
    icon: "material-symbols:manufacturing",
    isPrefix: false,
    path: upNavRoutePaths.settings.path,
    childRoutes: [...navbarOptions.settings] as Route[],
  },
  Help: {
    name: "Help",
    type: "sidebarOption",
    dataTarget: "Help Button",
    translationKey: "sidebar.help",
    icon: "material-symbols:help-outline",
    isPrefix: false,
  },
  Logout: {
    name: "Logout",
    type: "sidebarOption",
    dataTarget: "Logout",
    translationKey: "sidebar.logout",
    icon: "material-symbols:logout",
    isPrefix: false,
  },
};

export { upNavRoutePaths, sidebarOptions as upNavRouteConfig };
